import { GCInstant } from 'src/lib/gcinstant';
import StateObserver from 'src/StateObserver';
import { FullSequenceId } from '../ActionSequence';
import { CareItemID } from 'src/replicant/care/care.ruleset';
import { DaycareId } from 'src/replicant/daycare';
import features from 'src/replicant/ruleset/features';
import {
  pinchActions,
  PinchEventTrigger,
  PinchResource,
  PinchTrigger,
} from './pinch/pinch.redux';
import * as History from 'src/replicant/history';
import * as Pet from 'src/replicant/pet';
import { PetEvolutionStage } from 'src/replicant/data/PetSpecies.schema';
import {
  MarketingEventID,
  MarketingEventReward,
} from 'src/replicant/airtable/marketing.types';
import { GachaID } from 'src/replicant/gacha/gacha.ruleset';
import { startPopupCount } from 'src/state/ui';
import { track } from './utils';

////////////////////////////////////////////////////////////////////////////////
// Dialog open/close events
////////////////////////////////////////////////////////////////////////////////
const DIALOG_BLACKLIST = [
  'popupAction',
  'popupResult',
  'popupTutorial',
  'popupConversationScript',
];

export function trackDialogOpen(props: { id: string } & Record<string, any>) {
  if (DIALOG_BLACKLIST.includes(props.id)) return;
  const state = StateObserver.getState().user;
  if (features(state).amplitudeEventThrottle) return;

  track('DialogOpen', props);
}

export function trackDialogClose(props: { id: string } & Record<string, any>) {
  if (DIALOG_BLACKLIST.includes(props.id)) return;
  const state = StateObserver.getState().user;
  if (features(state).amplitudeEventThrottle) return;

  track('DialogClose', props);
}

////////////////////////////////////////////////////////////////////////////////
// Sequences analytics
////////////////////////////////////////////////////////////////////////////////
export function trackSequenceFinish(id: FullSequenceId) {
  const state = StateObserver.getState();
  const popupCount = state.ui.popupCount;
  if (features(state.user).amplitudeEventThrottle) {
    StateObserver.dispatch(startPopupCount());
    return;
  }

  track('SequenceFinish', { id, popupCount });
  StateObserver.dispatch(startPopupCount());
}

export function trackSequenceStart(id: FullSequenceId) {
  StateObserver.dispatch(startPopupCount());
  const state = StateObserver.getState().user;
  if (features(state).amplitudeEventThrottle) {
    return;
  }

  track('SequenceStart', { id });
}

////////////////////////////////////////////////////////////////////////////////
// A new pet has spawned
////////////////////////////////////////////////////////////////////////////////
export function trackPetSpawn({
  feature, // The feature that triggered the spawn
  subFeature,
}:
  | {
      feature: 'new_game';
      subFeature: 'new_game'; // For new_game feature
    }
  | {
      feature: 'death';
      subFeature: // For death feature
        'death_baby' | 'death_kid' | 'death_youth' | 'death_adult';
    }
  | {
      feature: 'debut';
      subFeature: 'debut_inviter' | 'debut_invitee';
    }) {
  const { user } = StateObserver.getState();
  const species = Pet.getters.getCurrentEvolution(user)?.species;

  if (!species) {
    return;
  }

  const history = History.getters.getRaisedPetsHistory(user);

  const raised = history[species] ?? 0;
  track('PetSpawn', {
    feature,
    isNew: raised === 1,
    $subFeature: subFeature,
  });
}

////////////////////////////////////////////////////////////////////////////////
// Track Pinch
////////////////////////////////////////////////////////////////////////////////
type PinchItemID = CareItemID | DaycareId | PetEvolutionStage | GachaID;
type PinchProductID =
  | `energy_${string}`
  | `bPoints_${string}`
  | `event_energy_${string}`;

export function trackPinchEvent(
  args: {
    resource: PinchResource;
    trigger: PinchTrigger;
    item_id?: PinchItemID;
    product_id?: PinchProductID;
  },
  additionalData: Object = {},
) {
  GCInstant.trackPinch({ ...args, ...additionalData });

  StateObserver.dispatch(
    pinchActions.setLatestPinch({
      trigger: args.trigger as PinchEventTrigger,
      resource: args.resource,
      allTimePinchCount: GCInstant.storage.trackPinchAllTimeCount ?? 0,
      allTimePinchCountForResource:
        GCInstant.storage.trackPinchAllTimeCounts[args.resource] ?? 0,
    }),
  );
}

////////////////////////////////////////////////////////////////////////////////
// Track Pinch Conversion
////////////////////////////////////////////////////////////////////////////////
type PinchConversionAction =
  | 'viral'
  | 'social'
  | 'purchase'
  | 'ad_view'
  | 'in_game';

export function trackPinchConversion(args: {
  action: PinchConversionAction;
  feature: string;
  subFeature: string;
  resource: PinchResource;
  async?: boolean;
}) {
  const { pinch } = StateObserver.getState();

  const pinchedResources = Object.keys(pinch.events).filter(
    (resource) => !!pinch.events[resource as keyof typeof pinch.events],
  );

  // The player is pinched if any resource is pinched
  if (!pinchedResources.length) {
    return;
  }

  const { resource } = args;
  const matchingPinchEvent = pinch.events[resource];

  // The associated pinch doesn't exist (that is when the particular resources isn't pinched)
  if (!matchingPinchEvent) {
    track('ImplicitPinchConversion', {
      ...args,
      pinchedResources: pinchedResources.join(';'),
      breadcrumbs: pinch.breadcrumbs.join(';'),
      pinchAssociated: false, // Always false
    });
    return;
  }

  if (args.async) {
    track('PinchConversionAsync', {
      ...args,
      ...matchingPinchEvent,
      pinchedResources: pinchedResources.join(';'),
      breadcrumbs: pinch.breadcrumbs.join(';'),
      conversionCount: matchingPinchEvent.conversionCount + 1,
      pinchAssociated: true, // Always true in  this case
    });
  } else {
    track('PinchConversion', {
      ...args,
      ...matchingPinchEvent,
      pinchedResources: pinchedResources.join(';'),
      breadcrumbs: pinch.breadcrumbs.join(';'),
      conversionCount: matchingPinchEvent.conversionCount + 1,
      pinchAssociated: true, // Always true in  this case
    });
  }

  StateObserver.dispatch(pinchActions.setPinchConversion(resource));
}

////////////////////////////////////////////////////////////////////////////////
// Tracking care action finish event
////////////////////////////////////////////////////////////////////////////////
export function trackingCareActionFinishEvent(
  eventProperties:
    | {
        action_type: string;
        item_id: string;
        result?: 'success' | 'failure';
        withFriends: false;
      }
    | {
        action_type: string;
        item_id: string;
        result?: 'success' | 'failure';
        withFriends: true;
        friendID?: string;
        friendPetID?: string | null;
        friendPetStomach?: number;
        friendPetLove?: number;
        friendDirtiness?: number;
        friendUrine?: number;
        friendStress?: number;
        friendToothAcidity?: number;
        friendAge?: number;
        friendPlayerLevel?: number;
        visits?: number;
        visited?: number;
        bPointsConsumed: number;
      },
) {
  if (eventProperties.withFriends) {
    const { friendPetID } = eventProperties;

    track('CareActionFinish', {
      ...eventProperties,
      friendPetID: friendPetID === null ? undefined : friendPetID,
    });
  } else {
    track('CareActionFinish', {
      ...eventProperties,
    });
  }
}

////////////////////////////////////////////////////////////////////////////////
// User collected reward
////////////////////////////////////////////////////////////////////////////////
export function trackMarketingRewardCollected(
  pageLinkId: MarketingEventID,
  rewards: MarketingEventReward,
) {
  track('PageMarketing_Rewards_Collect', {
    pageLinkId,
    coins: rewards.coins || 0,
    gems: rewards.gems || 0,
  });
}

////////////////////////////////////////////////////////////////////////////////
// User pressed ok button on error message
////////////////////////////////////////////////////////////////////////////////
export function trackMarketingEventCollectError(
  pageLinkId: string,
  errorName: 'already-received' | 'expired' | 'wrong-id',
) {
  track('PageMarketing_Sorry_Okay', { pageLinkId, errorName });
}
